import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useWeb3 } from '@/hooks/use-web3';
import { PoolState } from '@/lib/common/name-space';
import { PoolListType } from '@/types';
import { promiseAllSettled } from '@/lib/Promise';

import Box from '@mui/material/Box';

import Typography from '@mui/material/Typography';
import CommonButton from '../CommonButton';
import { useTheme } from '@mui/material';
import StakingModal from '../modal/StakingModal';
import SymbolSvg from '@/assets/assetfi/Symbol';
import BigNumber from 'bignumber.js';
import { toast } from 'react-toastify';
import { isPrivateToken } from '@/lib/web3/privateTokens';
import CommonModal from '../modal/CommonModal';
import ExplainDetail from './ExplainDetail';
import { useWeb3AuthConnect } from '@web3auth/modal/react';
import { poolAmount } from '@/api/staking/read';
import { useTickerStore } from '@/store/zustand/ticker';

export default function PoolExplain({
  contractAddr,
  detail: detailInfo,
}: {
  contractAddr: string;
  detail: PoolListType | null;
}) {
  const [detail, setDetail] = useState<PoolListType | null>(null);
  const [totalStakeUsdt, setTotalStakeUsdt] = useState<string>('0');
  const [address, setAddress] = useState('');
  const [isOpenStakingModal, setIsOpenStakingModal] = useState(false);
  const [isShowStakeBtn, setIsShowStakeBtn] = useState(false);
  const [usdt, setUsdt] = useState('0');
  const [isPrivateOwn, setIsPrivateOwn] = useState(true); // 일반회원도 있으므로 true 시작
  const { web3authCall, address: addr } = useWeb3();
  const { isConnected } = useWeb3AuthConnect();
  const theme = useTheme();

  const location = useLocation();
  const path = location.pathname;
  const { getSymbolData } = useTickerStore();

  useEffect(() => {
    if (detailInfo && detail === null) {
      setDetail(detailInfo);
      (async () => {
        setUsdt(getSymbolData('SFL')?.usdt || '0');
      })();
      if (
        path.startsWith('/poolDetail') &&
        (detailInfo.state === PoolState.POOLSTATE_FUNDRAISING || detailInfo.state === PoolState.POOLSTATE_OPERATING)
      ) {
        setIsShowStakeBtn(true);
      }
    }
    return () => {};
  }, [detailInfo, detail, path]);

  // /poolDetail /portfolioDetail

  // TODO 경로를 받아서 portfolio일 경우 리워드 전체 받기로 변경

  // useEffect(() => {
  //   if (path.startsWith('/poolDetail') && detail?.state === PoolState.POOLSTATE_FUNDRAISING) {
  //     setIsShowStakeBtn(true);
  //   }
  //   return () => {};
  // }, [path, detail]);

  useEffect(() => {
    if (isConnected && addr) {
      setAddress(addr);
    }

    return () => {};
  }, [addr]);

  useEffect(() => {
    if (!contractAddr) {
      // 이전화면으로 이동
      return;
    }
    if (detail) {
      return;
    }
    (async () => {
      const arrPromise = [];

      arrPromise.push(() => poolAmount(contractAddr));
      try {
        const result = await promiseAllSettled(arrPromise);
        setTotalStakeUsdt(result[0].data);
      } catch (e) {
        console.log(e);
      }
    })();
    return () => {};
  }, [detail, contractAddr]);

  const handleClick = async () => {
    if (!detail) {
      return;
    }
    // 1차 예외 처리
    if (BigNumber(detail.goalMaxUsdt).lt(totalStakeUsdt)) {
      toast.error('The maximum fundraising amount of the pool has been exceeded, so staking is not possible.');
      return;
    }
    // 서버에서 받아온 토큰과 컨트랙트를 가지고 체크
    if (detail.extraContractAddr && detail.privateTokenSymbol) {
      const payloads = {
        web3authCall: web3authCall!,
        address,
        symbol: detail.privateTokenSymbol,
      };
      const { isOwn } = await isPrivateToken(payloads);
      if (isOwn === false) {
        setIsPrivateOwn(isOwn);
        // 조건에 부합하지 않음
        return;
      }
    }

    setIsOpenStakingModal(true);
  };

  if (!detail) {
    return <div></div>;
  }
  return (
    <>
      {/* PC */}
      <Box
        sx={{
          marginTop: '24px',
          border: `0.5px solid ${theme.greyscale.lightgrey1}`,
          borderRadius: '20px',
          padding: '24px',
          display: { xs: 'none', md: 'flex' },
          gap: '24px',
          justifyContent: 'space-between',
        }}
      >
        {/* 좌측 영역 */}
        <Box>
          <Box>
            <Typography variant="body2" color={theme.greyscale.darkgrey2}>
              {detail.description}
            </Typography>
          </Box>

          <Box sx={{ marginTop: '24px' }}>
            <Typography variant="subtitle1" color={theme.greyscale.grey3}>
              Total Staked
            </Typography>
            <Typography component={'div'} variant="h4" color={theme.greyscale.darkgrey2}>
              {totalStakeUsdt}{' '}
              <Typography display="inline" component={'div'} variant="h6" color={theme.greyscale.darkgrey2}>
                USD
              </Typography>
            </Typography>

            <Typography component={'div'} variant="h5" color={theme.greyscale.darkgrey2}>
              ~ {BigNumber(totalStakeUsdt).dividedBy(usdt).toFixed(2)}{' '}
              <Typography display="inline" component={'div'} variant="body3" color={theme.greyscale.darkgrey2}>
                SFL
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* 우측 영역 */}
        <Box>
          <Typography variant="subtitle1" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
              minWidth: '335px',
            }}
          >
            <ExplainDetail detail={detail} />
          </Box>
          {isShowStakeBtn &&
            address &&
            (detail.state === PoolState.POOLSTATE_FUNDRAISING || detail.state === PoolState.POOLSTATE_OPERATING) && (
              <CommonButton
                sx={{ marginTop: '16px', width: '100%', borderRadius: '10px' }}
                color={address ? '#ffffff' : theme.background.blue}
                bgColor={address ? theme.background.blue : '#ffffff'}
                text={address ? 'Stake' : 'Connect Wallet'}
                handleClick={handleClick}
                border={theme.background.blue}
                variant={address ? 'h5' : 'h6'}
              />
            )}
        </Box>
      </Box>
      {/* ------------------------------------------------------------------------------------------------------ */}
      {/* Mobile */}

      <Box
        sx={{
          marginTop: '24px',
          border: `0.5px solid ${theme.greyscale.lightgrey1}`,
          borderRadius: '20px',
          padding: '24px',
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          gap: '24px',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <SymbolSvg width={24} height={24} />
          <Box>
            <Typography color={theme.greyscale.darkgrey2} variant={'h6'}>
              {detail.title}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Box>
            <Typography variant="subtitle2" color={theme.greyscale.grey3}>
              Pool Description
            </Typography>
            <Typography variant="body2" color={theme.greyscale.darkgrey2}>
              {detail.description}
            </Typography>
          </Box>

          <Box sx={{ marginTop: '24px' }}>
            <Typography variant="subtitle2" color={theme.greyscale.grey3}>
              Total Staked
            </Typography>
            <Typography component={'div'} variant="h4" color={theme.greyscale.darkgrey2}>
              {totalStakeUsdt}{' '}
              <Typography display="inline" variant="h6" color={theme.greyscale.darkgrey2}>
                USD
              </Typography>
            </Typography>

            <Typography component={'div'} variant="h5" color={theme.greyscale.darkgrey2}>
              ~ {BigNumber(totalStakeUsdt).dividedBy(usdt).toFixed(2)}{' '}
              <Typography display="inline" variant="subtitle2" color={theme.greyscale.darkgrey2}>
                SFL
              </Typography>
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="subtitle2" color={theme.greyscale.grey3}>
            Details
          </Typography>

          <Box
            sx={{
              marginTop: '8px',
              background: theme.background.default,
              borderRadius: '10px',
              border: `0.5px solid ${theme.greyscale.grey1}`,
            }}
          >
            <ExplainDetail detail={detail} />
          </Box>
          {isShowStakeBtn &&
            address &&
            (detail.state === PoolState.POOLSTATE_FUNDRAISING || detail.state === PoolState.POOLSTATE_OPERATING) && (
              <CommonButton
                sx={{ marginTop: '16px', width: '100%', borderRadius: '10px' }}
                color={address ? '#ffffff' : theme.background.blue}
                bgColor={address ? theme.background.blue : '#ffffff'}
                text={address ? 'Stake' : 'Connect Wallet'}
                handleClick={handleClick}
                border={theme.background.blue}
                variant={address ? 'h5' : 'h6'}
              />
            )}
        </Box>
      </Box>
      {/* Staking Modal */}
      <StakingModal
        open={isOpenStakingModal}
        handleClose={() => setIsOpenStakingModal(false)}
        contractAddr={contractAddr}
        detail={detail}
        totalStakeUsdt={totalStakeUsdt}
      />

      <CommonModal
        open={!isPrivateOwn}
        handleClose={() => {
          setIsPrivateOwn(true);
        }}
        title="Restricting Service Participation"
        type="error"
      >
        <Typography color={theme.greyscale.grey2} variant="h6" textAlign={'left'}>
          This staking pool is only available to those who are authorized to use it.
          <br /> <br />
          Thank You.
        </Typography>
      </CommonModal>
    </>
  );
}
